import React, { useEffect, useState } from "react";
import { HardDrive, AlertTriangle, X, Cloud, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "../lib/api";

function fmtBytes(n) {
    if (n == null) return "—";
    if (n >= 1024 * 1024 * 1024) return `${(n / (1024 * 1024 * 1024)).toFixed(1)} GB`;
    if (n >= 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`;
    if (n >= 1024) return `${Math.round(n / 1024)} KB`;
    return `${n} B`;
}

/**
 * Compact storage meter intended for the editor toolbar.
 * Click → opens the upgrade modal.
 */
export function StorageMeter({ usage, onClick, compact = false }) {
    if (!usage) return null;
    const pct = usage.percent ?? 0;
    let tone = "text-ink-soft border-ink/15 bg-white";
    if (pct >= 90) tone = "text-signal-red border-signal-red/40 bg-signal-red/5";
    else if (pct >= 70) tone = "text-signal-orange border-signal-orange/40 bg-signal-orange/10";
    return (
        <button
            onClick={onClick}
            className={`btn-brutal inline-flex items-center gap-2 px-2.5 py-1.5 ui-heading text-[11px] uppercase tracking-widest border ${tone}`}
            data-testid="storage-meter"
            title={`${fmtBytes(usage.used_bytes)} of ${fmtBytes(usage.quota_bytes)} used`}
        >
            {pct >= 90 ? <AlertTriangle size={12} /> : <HardDrive size={12} />}
            <span>{fmtBytes(usage.used_bytes)} / {fmtBytes(usage.quota_bytes)}</span>
            {!compact && <span className="text-ink-muted normal-case tracking-normal">{pct.toFixed(0)}%</span>}
        </button>
    );
}

export function StorageBar({ usage }) {
    if (!usage) return null;
    const pct = Math.min(100, usage.percent ?? 0);
    let bar = "bg-cobalt";
    if (pct >= 90) bar = "bg-signal-red";
    else if (pct >= 70) bar = "bg-signal-orange";
    return (
        <div className="w-full" data-testid="storage-bar">
            <div className="flex justify-between text-xs text-ink-soft mb-1.5">
                <span className="font-mono">{fmtBytes(usage.used_bytes)} of {fmtBytes(usage.quota_bytes)}</span>
                <span className="font-mono">{pct.toFixed(0)}%</span>
            </div>
            <div className="h-2 bg-ink/10 border border-ink/10 overflow-hidden">
                <div className={`h-full ${bar} transition-all duration-300`} style={{ width: `${pct}%` }} />
            </div>
            {usage.bonus_bytes > 0 && (
                <p className="mt-2 text-[11px] text-ink-muted ui-heading uppercase tracking-widest">
                    Includes +{fmtBytes(usage.bonus_bytes)} from upgrade
                </p>
            )}
        </div>
    );
}

/**
 * Upgrade modal — fetches the public storage-pack catalogue and starts a Stripe Checkout.
 */
export function StorageUpgradeModal({ open, onClose, currentUsage }) {
    const [packs, setPacks] = useState([]);
    const [loadingPack, setLoadingPack] = useState(null);

    useEffect(() => {
        if (!open) return;
        let cancelled = false;
        (async () => {
            try {
                const { data } = await api.get("/payments/storage-packs");
                if (!cancelled) setPacks(data.packs || []);
            } catch (e) { /* ignore */ }
        })();
        return () => { cancelled = true; };
    }, [open]);

    const buyPack = async (packId) => {
        try {
            setLoadingPack(packId);
            const { data } = await api.post("/payments/storage-checkout", {
                origin_url: window.location.origin,
                pack: packId,
            });
            window.location.href = data.url;
        } catch (e) {
            toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not start checkout");
            setLoadingPack(null);
        }
    };

    if (!open) return null;

    return (
        <div className="fixed inset-0 bg-ink/40 z-50 flex items-center justify-center p-4" data-testid="storage-upgrade-modal">
            <div className="bg-white border border-ink w-full max-w-lg p-6 shadow-brutal">
                <div className="flex items-start justify-between mb-4">
                    <div>
                        <div className="ui-heading text-xs uppercase tracking-widest text-ink-muted mb-1">/ cloud storage</div>
                        <h3 className="font-display font-black text-2xl tracking-tighter">Need more space?</h3>
                    </div>
                    <button onClick={onClose} aria-label="Close" data-testid="storage-modal-close"><X size={18} /></button>
                </div>

                {currentUsage && (
                    <div className="mb-5 border border-ink/10 bg-paper p-4">
                        <StorageBar usage={currentUsage} />
                    </div>
                )}

                <p className="text-sm text-ink-soft mb-4">
                    Pay once. Storage is added to your account forever — no subscriptions.
                </p>

                <div className="grid gap-3">
                    {packs.map((p) => (
                        <button
                            key={p.id}
                            onClick={() => buyPack(p.id)}
                            disabled={!!loadingPack}
                            className="btn-brutal flex items-center gap-3 border border-cobalt text-left bg-white hover:bg-cobalt/5 px-4 py-3 disabled:opacity-60"
                            data-testid={`storage-buy-${p.id}`}
                        >
                            <Cloud size={18} className="text-cobalt shrink-0" />
                            <div className="flex-1">
                                <div className="ui-heading text-sm font-semibold">{p.label}</div>
                                <div className="text-xs text-ink-muted">One-time payment · added to your quota</div>
                            </div>
                            <div className="font-display font-black text-2xl tracking-tighter">${p.price_usd}</div>
                        </button>
                    ))}
                </div>

                <p className="mt-5 text-xs text-ink-muted">
                    <CheckCircle2 size={12} className="inline mr-1 text-cobalt" />
                    Local open / edit / download is always unlimited and never counts against your cloud quota.
                </p>
            </div>
        </div>
    );
}

/** Convenience hook so pages don't have to wire fetch themselves. */
export function useStorageUsage() {
    const [usage, setUsage] = useState(null);
    const refresh = React.useCallback(async () => {
        try {
            const { data } = await api.get("/account/storage");
            setUsage(data);
            return data;
        } catch (e) {
            return null;
        }
    }, []);
    useEffect(() => { refresh(); }, [refresh]);
    return { usage, refresh };
}
